import * as React from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { createServerFn } from '@tanstack/react-start'
import { useSuspenseQuery, queryOptions } from '@tanstack/react-query'

const renderAllTemplates = createServerFn({ method: 'GET' }).handler(async () => {
  const { render } = await import('@react-email/components')
  const { TEMPLATES } = await import('@/lib/email-templates/registry')

  const results: Array<{
    name: string
    displayName: string
    subject: string
    html: string
    error?: string
  }> = []

  for (const [name, entry] of Object.entries(TEMPLATES)) {
    const displayName = entry.displayName || name
    if (!entry.previewData) {
      results.push({ name, displayName, subject: '', html: '', error: 'No preview data' })
      continue
    }
    try {
      const html = await render(React.createElement(entry.component, entry.previewData))
      const subject =
        typeof entry.subject === 'function' ? entry.subject(entry.previewData) : entry.subject
      results.push({ name, displayName, subject, html })
    } catch (err) {
      results.push({
        name,
        displayName,
        subject: '',
        html: '',
        error: err instanceof Error ? err.message : String(err),
      })
    }
  }
  return results
})

const previewQuery = queryOptions({
  queryKey: ['email-preview-all'],
  queryFn: () => renderAllTemplates(),
  staleTime: 60_000,
})

export const Route = createFileRoute('/email-preview')({
  head: () => ({ meta: [{ title: 'Email templates preview' }, { name: 'robots', content: 'noindex, nofollow' }] }),
  loader: ({ context }) => context.queryClient.ensureQueryData(previewQuery),
  component: EmailPreviewPage,
})

function EmailPreviewPage() {
  const { data } = useSuspenseQuery(previewQuery)
  const [active, setActive] = React.useState(data[0]?.name ?? '')
  const current = data.find((t) => t.name === active) ?? data[0]

  return (
    <div style={{ minHeight: '100vh', background: '#0b2a4a', color: '#fff', fontFamily: 'system-ui, sans-serif' }}>
      <div style={{ padding: '24px 32px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>Email templates preview</h1>
        <p style={{ margin: '6px 0 0', opacity: 0.7, fontSize: 14 }}>
          Live render van alle {data.length} templates met witte Elvion-logo en huisstijlkleuren.
        </p>
      </div>

      <div
        style={{
          display: 'flex',
          gap: 4,
          padding: '12px 32px',
          flexWrap: 'wrap',
          background: '#081e36',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          alignItems: 'center',
        }}
      >
        <span style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', opacity: 0.5, marginRight: 8 }}>
          Templates
        </span>
        {data.map((t) => {
          const isActive = active === t.name
          return (
            <button
              key={t.name}
              onClick={() => setActive(t.name)}
              style={{
                padding: '8px 16px',
                borderRadius: 6,
                border: isActive ? '1px solid #1864c5' : '1px solid rgba(255,255,255,0.12)',
                background: isActive ? '#1864c5' : 'rgba(255,255,255,0.04)',
                color: isActive ? '#fff' : 'rgba(255,255,255,0.75)',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: isActive ? 600 : 400,
                transition: 'all 0.15s ease',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
              onMouseEnter={(e) => {
                if (!isActive) {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.10)'
                  e.currentTarget.style.color = '#fff'
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.04)'
                  e.currentTarget.style.color = 'rgba(255,255,255,0.75)'
                }
              }}
            >
              {t.displayName}
            </button>
          )
        })}
      </div>

      {current && (
        <div style={{ padding: '24px 32px' }}>
          <div style={{ marginBottom: 12, fontSize: 13, opacity: 0.85 }}>
            <strong>Onderwerp:</strong> {current.subject || '—'}
          </div>
          {current.error ? (
            <div style={{ padding: 16, background: '#7f1d1d', borderRadius: 8 }}>
              Render error: {current.error}
            </div>
          ) : (
            <iframe
              title={current.displayName}
              srcDoc={current.html}
              style={{
                width: '100%',
                height: 'calc(100vh - 220px)',
                border: 'none',
                background: '#fff',
                borderRadius: 8,
              }}
            />
          )}
        </div>
      )}
    </div>
  )
}
